/**
 * A shared URL survives signing in.
 *
 * Android hands a share to the WebView exactly once: MainActivity dispatches a
 * `sharedUrl` event and never repeats it, and because ACTION_SEND is not a URL
 * intent there is no launch URL to read it back from. Signing in then reloads
 * the page — native replaces the location after the token exchange, web
 * navigates away to the BFF — so a share held only in React state is gone by
 * the time the user returns, which is why sharing while signed out looked like
 * nothing happened.
 *
 * Parking it here bridges that gap. The TTL is long enough to walk through the
 * portal and short enough that a share abandoned days ago does not resurface
 * and submit itself.
 */

const STORAGE_KEY = 'sieve.share.pending';
const PENDING_SHARE_TTL_MS = 15 * 60 * 1000;

interface PendingShare {
  url: string;
  savedAt: number;
}

export const savePendingShare = (url: string): void => {
  if (!url) return;
  try {
    const payload: PendingShare = { url, savedAt: Date.now() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Storage can be unavailable; the share is simply lost as it was before.
  }
};

export const readPendingShare = (): string | undefined => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return undefined;

    const pending = JSON.parse(raw) as Partial<PendingShare>;
    if (!pending?.url || typeof pending.savedAt !== 'number') {
      clearPendingShare();
      return undefined;
    }

    if (Date.now() - pending.savedAt > PENDING_SHARE_TTL_MS) {
      clearPendingShare();
      return undefined;
    }

    return pending.url;
  } catch {
    clearPendingShare();
    return undefined;
  }
};

export const clearPendingShare = (): void => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
